import type {CmLogger} from "@cm/log"
import {extensionForContentType, isValidContentType} from "@cm/utils/content-types"
import type {PbrExport} from "@cm/material-maps-exporter"
import {DataObjectAssignmentType} from "@cm/utils/data-object-assignment"
import * as Types from "#material-outputs-filenames/types"
import {MATERIAL_THUMBNAIL_DIMENSIONS} from "@cm/utils/material-thumbnail-constants"
import * as Schemas from "#material-outputs-filenames/schemas/index"
import * as AssetDataAssembly from "#material-outputs-filenames/asset-data"

export namespace MaterialOutputsFilenames {
    export type MapType = Types.MapType

    export const MAPS_EXPORT_MAP_ASSET_TYPE = Types.MAPS_EXPORT_MAP_ASSET_TYPE
    export const MAPS_EXPORT_INFO_ASSET_TYPE = Types.MAPS_EXPORT_INFO_ASSET_TYPE

    export type RenderAssetType = Types.RenderAssetType
    export type MapsExportAssetType = Types.MapsExportAssetType
    export type MapsExportMapAssetType = Types.MapsExportMapAssetType
    export type MapsExportInfoAssetType = Types.MapsExportInfoAssetType
    export type AssetType = Types.AssetType

    export function isAssignmentTypeValidAssetType(x: DataObjectAssignmentType): x is RenderAssetType | MapsExportAssetType {
        return Types.isAssignmentTypeValidAssetType(x)
    }

    export function isRenderAssetType(x: AssetType): x is RenderAssetType {
        return Types.isRenderAssetType(x)
    }

    export type MaterialData = Types.MaterialData

    export type MaterialFieldsForAssetData = AssetDataAssembly.MaterialFieldsForAssetData
    export type PartialAssetData = AssetDataAssembly.PartialAssetData
    export const AssetNamingError = AssetDataAssembly.AssetNamingError
    export const partialAssetDataForMaterial = AssetDataAssembly.partialAssetDataForMaterial

    export type ImageResolution = Types.ImageResolution
    export type ImageFormat = Types.ImageFormat

    export type ExportSettings = Types.ExportSettings

    export type AssetDataT<T extends AssetType> = Types.AssetDataT<T>
    export type AssetData<T extends AssetType = AssetType> = Types.AssetData<T>

    export type AssetDataMapsExport = Types.AssetDataMapsExport

    export const UNDEFINED_FIELD_SYMBOL = Types.UNDEFINED_FIELD_SYMBOL
    export type FilenameSchema = Types.FilenameSchema
    export type FilenameSchemaEntry<T extends AssetType = AssetType> = Types.FilenameSchemaEntry<T>
    export type OrganizationName = Types.OrganizationName

    export const ASSETS_FILENAME_SCHEMAS_FOR_ORGANIZATION = Schemas.ASSETS_FILENAME_SCHEMAS_FOR_ORGANIZATION

    function formatArticleId(articleId: string) {
        // ArticleId contains rangeId/productId and colorId, possibly separated by a dash
        // ColorId is always represented by the last three digits of the articleId
        // Format articleId to always use dash between the rangeId/productId and colorId
        return articleId.at(-4) === "-" ? articleId : `${articleId.slice(0, -3)}-${articleId.slice(-3)}`
    }

    function canonicalImageFormatName(
        imageFormat: AssetData<RenderAssetType>["imageFormat"] | ExportSettings["format"],
        imageFormatSpelling?: NonNullable<Types.FilenameSchemaEntry["options"]>["imageFormatSpelling"],
    ) {
        if (imageFormat === "jpeg" || imageFormat === "jpg") return imageFormatSpelling?.jpeg ?? "jpg"
        else if (imageFormat === "tiff" || imageFormat === "tif") return imageFormatSpelling?.tiff ?? "tiff"
        return imageFormat
    }

    function canonicalInfoFileFormatName(fileFormat: PbrExport.Types.InfoFileFormat) {
        return fileFormat === "text" ? "txt" : fileFormat
    }

    export function ensureValidMediaType(mediaType: string | undefined | null) {
        if (isValidContentType(mediaType)) return extensionForContentType(mediaType)
        throw Error(`Invalid media type: ${mediaType}`)
    }

    function getRelevantPhysicalDimensions(assetData: AssetData) {
        const fallback = {widthCm: null, heightCm: null, widthIn: null, heightIn: null}

        const allUnitsFromIn = (widthIn: number, heightIn: number) => {
            return {
                widthCm: widthIn / Types.CM_TO_INCH,
                heightCm: heightIn / Types.CM_TO_INCH,
                widthIn,
                heightIn,
            }
        }

        const allUnitsFromCm = (widthCm: number, heightCm: number) => {
            return {
                widthCm,
                heightCm,
                widthIn: widthCm * Types.CM_TO_INCH,
                heightIn: heightCm * Types.CM_TO_INCH,
            }
        }

        switch (assetData.type) {
            case DataObjectAssignmentType.MaterialTileableRender:
            case DataObjectAssignmentType.MaterialMapsExport:
            case MAPS_EXPORT_INFO_ASSET_TYPE:
            case MAPS_EXPORT_MAP_ASSET_TYPE:
                if (assetData.materialData.widthCm === null || assetData.materialData.heightCm === null) return fallback
                return allUnitsFromCm(assetData.materialData.widthCm, assetData.materialData.heightCm)
            case DataObjectAssignmentType.MaterialThumbnail_10x10:
                return allUnitsFromCm(10, 10)
            case DataObjectAssignmentType.MaterialThumbnail_15x15:
                return allUnitsFromCm(15, 15)
            case DataObjectAssignmentType.MaterialThumbnail_20x20:
                return allUnitsFromCm(20, 20)
            case DataObjectAssignmentType.MaterialThumbnail_30x30:
                return allUnitsFromCm(30, 30)
            case DataObjectAssignmentType.MaterialThumbnail_7x7Inches:
                return allUnitsFromIn(7, 7)
            case DataObjectAssignmentType.MaterialThumbnail_9x9Inches:
                return allUnitsFromIn(9, 9)
            case DataObjectAssignmentType.MaterialThumbnail_16x16Inches:
                return allUnitsFromIn(16, 16)
            case DataObjectAssignmentType.MaterialThumbnail_32x32Inches:
                return allUnitsFromIn(32, 32)
            default:
                return fallback
        }
    }

    function imageSizePxForMaterialThumbnailAssetData(assetData: AssetDataT<Types.MaterialThumbnailType>): number {
        switch (assetData.imageResolution) {
            case "original": {
                const entry = Object.entries(MATERIAL_THUMBNAIL_DIMENSIONS).find((entry) => entry[0] === assetData.type)
                if (entry === undefined) {
                    throw Error(`No dimensions found for MaterialThumbnailType ${assetData.type} at original resolution`)
                }
                return entry[1].resolution
            }
            case "px500":
                return 500
            case "px1000":
                return 1000
            case "px2000":
                return 2000
            default:
                throw Error(`Unsupported image resolution ${assetData.imageResolution} for MaterialThumbnailType`)
        }
    }

    function formatFloat(value: number, fractionDigits: number, decimalSeparator: string) {
        return value.toFixed(fractionDigits).toString().replace(".", decimalSeparator)
    }

    function formatInt(value: number) {
        return parseInt(value.toString()).toString()
    }

    export function getFilenameForAssetData(assetData: AssetData) {
        if (!Object.keys(ASSETS_FILENAME_SCHEMAS_FOR_ORGANIZATION).includes(assetData.organizationName)) return null

        const isMapsExportAssetData = (x: AssetData): x is AssetDataT<MapsExportAssetType> => {
            return x.type === DataObjectAssignmentType.MaterialMapsExport
        }

        const isMapsExportMapAssetData = (x: AssetData): x is AssetDataT<MapsExportMapAssetType> => {
            return x.type === MAPS_EXPORT_MAP_ASSET_TYPE
        }

        const isMapsExportInfoAssetData = (x: AssetData): x is AssetDataT<MapsExportInfoAssetType> => {
            return x.type === MAPS_EXPORT_INFO_ASSET_TYPE
        }

        const isRenderAssetData = (x: AssetData): x is AssetDataT<RenderAssetType> => {
            return isRenderAssetType(x.type)
        }

        const isMaterialThumbnailAssetData = (x: AssetData): x is AssetDataT<Types.MaterialThumbnailType> => {
            return Types.isMaterialThumbnailType(x.type)
        }

        const isMapsExportFilenameSchemaEntry = (x: Types.FilenameSchemaEntry): x is Types.FilenameSchemaEntry<MapsExportAssetType> => {
            return x.type === DataObjectAssignmentType.MaterialMapsExport
        }

        const isMapsExportMapFilenameSchemaEntry = (x: Types.FilenameSchemaEntry): x is Types.FilenameSchemaEntry<MapsExportMapAssetType> => {
            return x.type === MAPS_EXPORT_MAP_ASSET_TYPE
        }

        const isMapsExportInfoFilenameSchemaEntry = (x: Types.FilenameSchemaEntry): x is Types.FilenameSchemaEntry<MapsExportInfoAssetType> => {
            return x.type === MAPS_EXPORT_INFO_ASSET_TYPE
        }

        const isRenderAssetFilenameSchemaEntry = (x: Types.FilenameSchemaEntry): x is Types.FilenameSchemaEntry<RenderAssetType> => {
            return isRenderAssetType(x.type)
        }

        // TODO Correctly narrow down T
        const checkPropertyMatch = <T>(entryProperty: T | undefined, dataProperty: T) => {
            return entryProperty === undefined || entryProperty === dataProperty
        }

        const schemaEntry = ASSETS_FILENAME_SCHEMAS_FOR_ORGANIZATION[assetData.organizationName]
            .filter((entry) => entry.type === assetData.type)
            .find((entry) => {
                let check = checkPropertyMatch(entry.isOtherInfoSet, assetData.materialData.rangeInfo.otherInfo !== null)
                if (isMapsExportAssetData(assetData) && isMapsExportFilenameSchemaEntry(entry)) {
                    check =
                        check &&
                        checkPropertyMatch(entry.exportFormat, assetData.exportSettings.format) &&
                        checkPropertyMatch(entry.exportResolution, assetData.exportSettings.resolution)
                } else if (isMapsExportMapAssetData(assetData) && isMapsExportMapFilenameSchemaEntry(entry)) {
                    check =
                        check &&
                        checkPropertyMatch(entry.exportFormat, assetData.exportSettings.format) &&
                        checkPropertyMatch(entry.exportResolution, assetData.exportSettings.resolution) &&
                        checkPropertyMatch(entry.mapType, assetData.mapType)
                } else if (isMapsExportInfoAssetData(assetData) && isMapsExportInfoFilenameSchemaEntry(entry)) {
                    check =
                        check &&
                        checkPropertyMatch(entry.exportFormat, assetData.exportSettings.format) &&
                        checkPropertyMatch(entry.exportResolution, assetData.exportSettings.resolution) &&
                        checkPropertyMatch(entry.fileFormat, assetData.fileFormat)
                } else if (isRenderAssetData(assetData) && isRenderAssetFilenameSchemaEntry(entry)) {
                    check =
                        check &&
                        checkPropertyMatch(entry.imageFormat, assetData.imageFormat) &&
                        checkPropertyMatch(entry.imageResolution, assetData.imageResolution)
                } else {
                    throw Error("Invalid assetData/schemaFilenameEntry combination")
                }
                return check
            })

        if (schemaEntry === undefined) return null

        let filename = `${schemaEntry.schema}`

        const {widthCm, heightCm, widthIn, heightIn} = getRelevantPhysicalDimensions(assetData)

        const articleId = assetData.materialData.articleId
        const materialName = assetData.materialData.name
        const rangeName = assetData.materialData.rangeInfo.name
        const rangeOtherInfo = assetData.materialData.rangeInfo.otherInfo
        const revisionNumber = assetData.materialData.revisionNumber

        const articleId_ = articleId === null || articleId === "" ? `${UNDEFINED_FIELD_SYMBOL}` : articleId
        const formattedArticleId_ = articleId === null || articleId === "" ? `${UNDEFINED_FIELD_SYMBOL}` : formatArticleId(articleId)
        const materialName_ = materialName === null || materialName === "" ? `${UNDEFINED_FIELD_SYMBOL}` : materialName
        const rangeName_ = rangeName === null || rangeName === "" ? UNDEFINED_FIELD_SYMBOL : rangeName
        const rangeOtherInfo_ = rangeOtherInfo === null || rangeOtherInfo === "" ? UNDEFINED_FIELD_SYMBOL : rangeOtherInfo
        const revisionNumber_ = revisionNumber === null ? `${UNDEFINED_FIELD_SYMBOL}` : formatInt(revisionNumber)

        const fractionDigits = schemaEntry.options?.fractionDigits ?? 2
        const decimalSeparator = schemaEntry.options?.decimalSeparator ?? ","
        const widthCm_ = widthCm === null ? `${UNDEFINED_FIELD_SYMBOL}` : formatFloat(widthCm, fractionDigits, decimalSeparator)
        const heightCm_ = heightCm === null ? `${UNDEFINED_FIELD_SYMBOL}` : formatFloat(heightCm, fractionDigits, decimalSeparator)
        const widthIn_ = widthIn === null ? `${UNDEFINED_FIELD_SYMBOL}` : formatFloat(widthIn, fractionDigits, decimalSeparator)
        const heightIn_ = heightIn === null ? `${UNDEFINED_FIELD_SYMBOL}` : formatFloat(heightIn, fractionDigits, decimalSeparator)

        const replaceAll = (str: string, pattern: string, newValue: string): string => {
            // TODO: use regex (a bit tricky due to the usage of $ symbol)
            return str.includes(pattern) ? replaceAll(str.replace(pattern, newValue), pattern, newValue) : str
        }

        filename = replaceAll(filename, "$MATERIAL_ARTICLE_ID$", articleId_)
        filename = replaceAll(filename, "$MATERIAL_FORMATTED_ARTICLE_ID$", formattedArticleId_)
        filename = replaceAll(filename, "$MATERIAL_NAME$", materialName_)
        filename = replaceAll(filename, "$MATERIAL_RANGE_NAME$", rangeName_)
        filename = replaceAll(filename, "$MATERIAL_RANGE_OTHER_INFO$", rangeOtherInfo_)
        filename = replaceAll(filename, "$MATERIAL_REVISION_NUMBER$", revisionNumber_)

        filename = replaceAll(filename, "$WIDTH_CM$", widthCm_)
        filename = replaceAll(filename, "$HEIGHT_CM$", heightCm_)
        filename = replaceAll(filename, "$WIDTH_IN$", widthIn_)
        filename = replaceAll(filename, "$HEIGHT_IN$", heightIn_)

        if (isMapsExportAssetData(assetData)) {
            filename = replaceAll(
                filename,
                "$EXPORT_FORMAT$",
                canonicalImageFormatName(assetData.exportSettings.format, schemaEntry.options?.imageFormatSpelling),
            )
            filename = replaceAll(filename, "$EXPORT_RESOLUTION$", assetData.exportSettings.resolution)
        } else if (isMapsExportMapAssetData(assetData)) {
            filename = replaceAll(
                filename,
                "$EXPORT_FORMAT$",
                canonicalImageFormatName(assetData.exportSettings.format, schemaEntry.options?.imageFormatSpelling),
            )
            filename = replaceAll(filename, "$EXPORT_RESOLUTION$", assetData.exportSettings.resolution)
            filename = replaceAll(filename, "$MAP_TYPE$", assetData.mapType)
            filename = replaceAll(filename, "$MAP_TYPE_UNDERSCORE_SEPARATED$", assetData.mapType.replace("-", "_"))
        } else if (isMapsExportInfoAssetData(assetData)) {
            filename = replaceAll(
                filename,
                "$EXPORT_FORMAT$",
                canonicalImageFormatName(assetData.exportSettings.format, schemaEntry.options?.imageFormatSpelling),
            )
            filename = replaceAll(filename, "$EXPORT_RESOLUTION$", assetData.exportSettings.resolution)
            filename = replaceAll(filename, "$INFO_FILE_FORMAT$", canonicalInfoFileFormatName(assetData.fileFormat))
        } else if (isRenderAssetData(assetData)) {
            filename = replaceAll(filename, "$IMAGE_FORMAT$", canonicalImageFormatName(assetData.imageFormat, schemaEntry.options?.imageFormatSpelling))
            if (isMaterialThumbnailAssetData(assetData)) {
                filename = replaceAll(filename, "$MATERIAL_THUMBNAIL_SIZE_PX$", imageSizePxForMaterialThumbnailAssetData(assetData).toString())
            }
        } else {
            throw Error("Invalid schemaFilenameEntry type")
        }

        return filename
    }

    // ----------- Validation ------------- //

    export const validateFilenameSchemasForOrganization = (organizationName: string, logger: CmLogger) => {
        const schemasEntryList = ASSETS_FILENAME_SCHEMAS_FOR_ORGANIZATION[organizationName]
        const validatedAssetTypes: AssetType[] = []

        let allOrganizationSchemasValid = true
        schemasEntryList.forEach((schemaEntry) => {
            if (validatedAssetTypes.includes(schemaEntry.type)) return

            const schemaEntryListForAssetType = schemasEntryList.filter((x) => x.type === schemaEntry.type)

            const fullKeySet = new Set<string>()
            schemaEntryListForAssetType.forEach((schemaEntry) => Object.keys(schemaEntry).map((entry) => fullKeySet.add(entry)))

            let schemaValid = true
            schemaEntryListForAssetType.forEach((schemaEntry) => {
                const entryKeySet = Object.keys(schemaEntry)
                const missingKeys = Array.from(fullKeySet).filter((key) => !entryKeySet.includes(key))

                if (missingKeys.length > 0) {
                    schemaValid = schemaValid && false
                    logger.log(`SchemaEntry:\n${JSON.stringify(schemaEntry)}\nMissing keys:\n${missingKeys.join(", ")}`)
                }
            })
            allOrganizationSchemasValid = allOrganizationSchemasValid && schemaValid

            validatedAssetTypes.push(schemaEntry.type)
        })
        logger.log(`Schema validation for organization ${organizationName} ${allOrganizationSchemasValid ? "PASSED" : "FAILED"}`)
        return allOrganizationSchemasValid
    }
}
